import api from "./index";
import SecureSS from "../utils/SecureStorage";
import {
  REASON_LABELS,
  OPEN_DISPUTE_STATUSES,
  CLIENT_SUBMITTED_ACTIONS,
  ADMIN_SUBMITTED_BY,
  CLIENT_SUBMITTED_BY,
  DISPUTE_STATUS,
  HISTORY_ACTION,
} from "../constants/disputes";
/**
 * Wallet Disputes API.
 *
 * Calls the ht-admin wrapper endpoints under `/1.0/wallet/disputes/...`, which forward to
 * wallet-service (`/api/v1/disputes/...`). wallet-service returns snake_case payloads; this
 * module maps them into the camelCase view-model the components consume, so the components
 * never touch the wire format.
 */
const validateUserPermissions = () => {
  const userProfile = SecureSS.getUserProfile();

  if (!userProfile) {
    return {
      isValid: false,
      error: "User not authenticated",
      shouldLogout: true,
    };
  }

  const { userType, role, clientId } = userProfile;

  if (userType !== "CLIENT") {
    return {
      isValid: false,
      error: "Unauthorized to access",
      shouldLogout: true,
    };
  }

  const allowedRoles = [
    "CLIENT_ADMIN",
    "CLIENT_FINANCE",
    "CLIENT_COMMERCIAL_TEAM",
  ];
  if (!allowedRoles.includes(role)) {
    return {
      isValid: false,
      error: "Not authorized to use the app",
      shouldLogout: true,
    };
  }

  if (!clientId) {
    return {
      isValid: false,
      error: "Client ID not found in user profile.",
      shouldLogout: true,
    };
  }

  return { isValid: true, clientId };
};

const requireClientId = () => {
  const validation = validateUserPermissions();
  if (!validation.isValid) {
    throw new Error(validation.error);
  }
  return validation.clientId;
};

/** Fresh idempotency key per logical user action (submit / each appeal / withdraw). */
const newRequestId = () => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID.
  return "req-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
};

// ---------- snake_case (wire) -> camelCase (view-model) mappers ----------

const reasonLabel = (code) => REASON_LABELS[code] || code;

const num = (v) => (v === null || v === undefined ? null : v);

const mapAttachment = (a) => ({
  attachmentId: a.attachment_id,
  fileName: a.file_name,
  contentType: a.content_type,
  sizeBytes: a.size_bytes,
});

const mapListRow = (raw) => {
  const agreed = raw.client_agreed_booking_amount || 0;
  // Disputed amount is pinned to the transacted amount AT dispute, not the live
  // value — matches the detail view and the column spec.
  const transactedAtDispute = raw.transacted_booking_amount_at_dispute || 0;
  return {
    id: raw.dispute_id,
    status: raw.status,
    disputedDate: raw.submitted_at,
    htConfirmationId: raw.ht_confirmation_code,
    htRoomConfirmationCode: raw.ht_room_confirmation_code,
    clientConfirmationId: raw.client_confirmation_id,
    propertyName: raw.property_name,
    currency: raw.currency,
    disputeReason: reasonLabel(raw.dispute_reason),
    declineReason: raw.decline_reason,
    clientAgreedBookingAmount: num(raw.client_agreed_booking_amount),
    transactedBookingAmountAtDispute: num(raw.transacted_booking_amount_at_dispute),
    currentTransactedBookingAmount: num(raw.current_transacted_booking_amount),
    platformFeeAtDispute: num(raw.platform_fee_at_dispute),
    currentPlatformFee: num(raw.current_platform_fee),
    disputedBookingAmount: Math.max(transactedAtDispute - agreed, 0),
    currentAppealRound: raw.current_appeal_round,
  };
};

const mapList = (raw, currency) => {
  const totals = raw.totals || [];
  const t = totals.find((x) => x.currency === currency) || totals[0] || {};
  return {
    total: raw.total || 0,
    pageSize: raw.page_size,
    nextPageUrl: raw.next_page_url,
    previousPageUrl: raw.previous_page_url,
    aggregates: {
      disputedBookingAmount: t.disputed_booking_amount || 0,
      feeImpact: t.fee_impact || 0,
    },
    data: (raw.data || []).map(mapListRow),
  };
};

const mapHistoryEntry = (h) => {
  const isClient = CLIENT_SUBMITTED_ACTIONS.includes(h.action);
  const reason =
    h.action === HISTORY_ACTION.DISPUTE_DECLINED
      ? h.decline_reason
      : reasonLabel(h.dispute_reason);
  return {
    action: h.action,
    date: h.action_date,
    reason,
    disputedBookingAmount: num(h.disputed_booking_amount),
    // Prefer the real acting user returned by wallet-service (relayed from the
    // X-Username header); fall back to the generic label otherwise.
    submittedBy: isClient
      ? h.submitted_by_user || CLIENT_SUBMITTED_BY
      : ADMIN_SUBMITTED_BY,
    issueDescription: h.issue_description,
    attachments: (h.attachments || []).map(mapAttachment),
    latestTransactedBookingAmount: num(h.transacted_booking_amount_snapshot),
    latestPlatformFee: num(h.platform_fee_snapshot),
    appealRound: h.appeal_round,
  };
};

const mapDetail = (raw) => {
  const history = (raw.history || []).map(mapHistoryEntry);
  // Detail has no top-level issue description — surface the latest client submission's.
  const latestClientEntry = [...history]
    .reverse()
    .find((h) => CLIENT_SUBMITTED_ACTIONS.includes(h.action));
  const agreed = raw.client_agreed_booking_amount || 0;
  const atDispute = raw.transacted_booking_amount_at_dispute || 0;
  return {
    id: raw.dispute_id,
    currency: raw.currency,
    status: raw.status,
    disputedDate: raw.submitted_at,
    htConfirmationId: raw.ht_confirmation_code,
    htRoomConfirmationCode: raw.ht_room_confirmation_code,
    clientConfirmationId: raw.client_confirmation_id,
    propertyName: raw.property_name,
    disputedBy: raw.property_name
      ? `Hotel Partner - ${raw.property_name}`
      : CLIENT_SUBMITTED_BY,
    disputeReason: reasonLabel(raw.dispute_reason),
    declineReason: raw.decline_reason,
    issueDescription: latestClientEntry?.issueDescription || "",
    transactedBookingAmount: num(raw.transacted_booking_amount_at_dispute),
    clientAgreedBookingAmount: num(raw.client_agreed_booking_amount),
    disputedBookingAmount: Math.max(atDispute - agreed, 0),
    latestTransactedBookingAmount: num(raw.current_transacted_booking_amount),
    platformFeeAtDispute: num(raw.platform_fee_at_dispute),
    latestPlatformFee: num(raw.current_platform_fee),
    currentAppealRound: raw.current_appeal_round,
    appealAllowed: raw.status === DISPUTE_STATUS.DECLINED,
    history,
  };
};

// ---------- raw fetch helpers ----------

const fetchListRaw = async ({
  clientId,
  currency,
  status,
  query,
  submittedAtGt,
  submittedAtLt,
  page,
  size,
}) => {
  const params = new URLSearchParams();
  if (status) params.append("status", status);
  if (currency) params.append("currency", currency);
  if (query) params.append("query", query);
  if (submittedAtGt) params.append("submitted_at_gt", submittedAtGt);
  if (submittedAtLt) params.append("submitted_at_lt", submittedAtLt);
  if (page) params.append("page", page);
  if (size) params.append("size", size);
  const qs = params.toString();
  const res = await api.getRequest(
    `/1.0/wallet/disputes/${clientId}${qs ? `?${qs}` : ""}`
  );
  return res.data;
};

// ---------- public API ----------

/**
 * List disputes for a currency + page, with per-currency rollup totals for the summary bar.
 * GET /1.0/wallet/disputes/{clientId}?currency=&status=&page=&size=
 */
export const getDisputes = async ({
  currency,
  status,
  query,
  submittedAtGt,
  submittedAtLt,
  page = 1,
  size = 10,
} = {}) => {
  const clientId = requireClientId();
  const raw = await fetchListRaw({
    clientId,
    currency,
    status,
    query,
    submittedAtGt,
    submittedAtLt,
    page,
    size,
  });
  return { status: 200, data: mapList(raw, currency) };
};

/**
 * Download the disputes list as a CSV blob, honoring the active filters.
 * Returns the raw (blob) axios response — the caller triggers the file save.
 * GET /1.0/wallet/disputes/{clientId}?...&export=true
 */
export const downloadDisputesCsv = async ({
  currency,
  status,
  query,
  submittedAtGt,
  submittedAtLt,
} = {}) => {
  const clientId = requireClientId();
  const params = new URLSearchParams();
  if (currency) params.append("currency", currency);
  if (status) params.append("status", status);
  if (query) params.append("query", query);
  if (submittedAtGt) params.append("submitted_at_gt", submittedAtGt);
  if (submittedAtLt) params.append("submitted_at_lt", submittedAtLt);
  params.append("export", "true");
  return api.getFileRequest(`/1.0/wallet/disputes/${clientId}?${params.toString()}`);
};

/**
 * Full dispute payload (financials + ordered history + attachment metadata).
 * GET /1.0/wallet/disputes/{clientId}/{disputeId}
 */
export const getDisputeDetail = async (disputeId) => {
  const clientId = requireClientId();
  const res = await api.getRequest(
    `/1.0/wallet/disputes/${clientId}/${disputeId}`
  );
  return { status: 200, data: mapDetail(res.data) };
};

/**
 * HT room confirmation codes that currently have a non-terminal dispute, used to disable
 * the entry-point Dispute button. Derived from the list endpoint (no dedicated endpoint).
 * Note: bounded to the first page (size 100); clients with more open disputes than that
 * would not all be reflected — acceptable for the gate, server still enforces on submit.
 */
export const getOpenDisputeIds = async ({ currency } = {}) => {
  const clientId = requireClientId();
  const raw = await fetchListRaw({ clientId, currency, size: 100, page: 1 });
  const rows = (raw.data || []).map(mapListRow);
  const ids = rows
    .filter((r) => OPEN_DISPUTE_STATUSES.includes(r.status))
    .map((r) => r.htRoomConfirmationCode)
    .filter(Boolean);
  return { data: { htConfirmationIds: ids } };
};

/**
 * Resolve the id of the OPEN dispute on a specific room confirmation code, so the
 * booking list can jump straight to viewing an already-raised dispute. Searches the
 * list server-side by room code (LIKE), then exact-matches in JS to avoid substring
 * collisions (e.g. "…-1" vs "…-11"). Returns { data: { id, status } } or { data: null }.
 */
export const findOpenDisputeByRoomCode = async ({
  currency,
  htRoomConfirmationCode,
} = {}) => {
  if (!htRoomConfirmationCode) return { data: null };
  const clientId = requireClientId();
  const raw = await fetchListRaw({
    clientId,
    currency,
    query: htRoomConfirmationCode,
    size: 100,
    page: 1,
  });
  const match = (raw.data || [])
    .map(mapListRow)
    .find(
      (r) =>
        r.htRoomConfirmationCode === htRoomConfirmationCode &&
        OPEN_DISPUTE_STATUSES.includes(r.status)
    );
  return { data: match ? { id: match.id, status: match.status } : null };
};

/**
 * Axios config that forwards the acting user's name to ht-admin, which relays it to
 * wallet-service so the dispute history records who took each action (instead of a
 * generic "Wallet user"). Returns undefined when no name is available.
 */
const actingUserConfig = () => {
  const profile = SecureSS.getUserProfile();
  const userName = profile?.username || profile?.email;
  return userName ? { headers: { "X-Username": userName } } : undefined;
};

/** Build a multipart payload: JSON part + attachment files. */
const buildMultipart = (jsonPartName, jsonBody, files = []) => {
  const formData = new FormData();
  formData.append(
    jsonPartName,
    new Blob([JSON.stringify(jsonBody)], { type: "application/json" })
  );
  files.forEach((file) => formData.append("attachments", file));
  return formData;
};

/**
 * Submit a new dispute.
 * POST /1.0/wallet/disputes/{clientId}  (multipart: `dispute` JSON + `attachments`)
 *
 * The booking-level `htConfirmationCode` is resolved by the ht-admin wrapper from the room
 * code; the UI only supplies `htRoomConfirmationCode` (= the booking row's ht_confirmation_id).
 */
export const submitDispute = (payload) => {
  const clientId = requireClientId();
  const body = {
    htRoomConfirmationCode: payload.htRoomConfirmationCode,
    clientAgreedBookingAmount: payload.clientAgreedBookingAmount,
    disputeReason: payload.disputeReason,
    issueDescription: payload.issueDescription || undefined,
    requestId: newRequestId(),
  };
  const formData = buildMultipart("dispute", body, payload.files);
  return api.postFileUploadRequest(
    `/1.0/wallet/disputes/${clientId}`,
    formData,
    actingUserConfig()
  );
};

/**
 * Appeal a declined dispute (only valid from DECLINED). Reopens it as UNDER_REVIEW.
 * POST /1.0/wallet/disputes/{clientId}/{disputeId}/appeal  (multipart: `appeal` JSON + `attachments`)
 */
export const appealDispute = (disputeId, payload) => {
  const clientId = requireClientId();
  const body = {
    clientAgreedBookingAmount: payload.clientAgreedBookingAmount,
    disputeReason: payload.disputeReason,
    issueDescription: payload.issueDescription || undefined,
    requestId: newRequestId(),
  };
  const formData = buildMultipart("appeal", body, payload.files);
  return api.postFileUploadRequest(
    `/1.0/wallet/disputes/${clientId}/${disputeId}/appeal`,
    formData,
    actingUserConfig()
  );
};

/**
 * Withdraw an open dispute (terminal). Not wired into the UI yet, exposed for completeness.
 * POST /1.0/wallet/disputes/{clientId}/{disputeId}/withdraw  (JSON)
 */
export const withdrawDispute = (disputeId, { comment } = {}) => {
  const clientId = requireClientId();
  const body = { requestId: newRequestId(), comment: comment || undefined };
  return api.postRequest(
    `/1.0/wallet/disputes/${clientId}/${disputeId}/withdraw`,
    body,
    actingUserConfig()
  );
};

/**
 * Mint a short-lived presigned download URL for an attachment (call per click, don't cache).
 * GET /1.0/wallet/disputes/{clientId}/attachments/{attachmentId}/download-url
 * @returns {Promise} { url, fileName, contentType, expiresAt }
 */
export const getAttachmentDownloadUrl = async (attachmentId) => {
  const clientId = requireClientId();
  const res = await api.getRequest(
    `/1.0/wallet/disputes/${clientId}/attachments/${attachmentId}/download-url`
  );
  const raw = res.data;
  return {
    url: raw.url,
    fileName: raw.file_name,
    contentType: raw.content_type,
    expiresAt: raw.expires_at,
  };
};
